import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { useThrottle } from "simplydo/react/hooks";
import { Pagination } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";

import type {
  IRole,
  PotentialRoleUser,
  PotentialRoleUsersPage,
  RoleUsersPage,
  RoleUsersQueryParams,
  RoleWithInformation,
  RolesUser,
} from "../types";

type RoleEditorAssignmentProps = {
  forType: OpenAPI.Schemas["Role"]["forType"];
  forId: string;
  usingRole: RoleWithInformation;
  setRoles: Dispatch<SetStateAction<RoleWithInformation[]>>;
};

const RoleEditorAssignment = ({
  forType,
  forId,
  setRoles,
  usingRole,
}: RoleEditorAssignmentProps) => {
  const [usersSearchState, setUsersSearchState] = useState<{
    loading: boolean;
    query: string;
    page: number;
  }>({ loading: false, query: "", page: 1 });
  const [potentialUsersSearchState, setPotentialUsersSearchState] = useState<{
    loading: boolean;
    query: string;
    page: number;
  }>({ loading: false, query: "", page: 1 });
  const [usersState, setUsersState] = useState<RoleUsersPage>({
    users: [],
    total: 0,
    nextPageAvailable: false,
    prevPageAvailable: false,
  });
  const [potentialUserState, setPotentialUserState] =
    useState<PotentialRoleUsersPage>({
      users: [],
      total: 0,
      nextPageAvailable: false,
      prevPageAvailable: false,
    });

  const user: RolesUser = useAppSelector((state) => state.user);
  const canViewDashboard = util.hasPermission(
    user,
    "org.viewDashboard",
    user?.ownerOrganisation?._id ?? "",
  );
  const canListUsers = util.hasPermission(
    user,
    "org.listUsers",
    user?.ownerOrganisation?._id ?? "",
  );

  const potentialUsersWithRole = useMemo(() => {
    if (!usingRole?._id) {
      return [];
    }
    return potentialUserState.users
      .filter((u) => (u.roleIds ?? []).includes(usingRole._id))
      .map((u) => u._id);
  }, [potentialUserState.users, usingRole]);

  const assignedUsersWithRole = useMemo(() => {
    if (!usingRole?._id) {
      return [];
    }
    return usersState.users
      .filter((u) => (u.roleIds ?? []).includes(usingRole._id))
      .map((u) => u._id);
  }, [usersState.users, usingRole]);

  const usingForId = forType === "global" ? "global" : forId;

  const fetchAssignedUsers = useThrottle(
    (query: string, page: number) => {
      if (!forId || !forType) return;

      api.roles.getAssignedRoleUsers(
        forType,
        forId,
        usingRole._id,
        { page, ...(query ? { query } : {}) },
        (data) => {
          setUsersSearchState((prevSearchState) => ({
            ...prevSearchState,
            loading: false,
          }));
          setUsersState((prevState) => ({
            ...prevState,
            users: data.users,
            total: data.total,
            nextPageAvailable: data.nextPageAvailable,
            prevPageAvailable: data.prevPageAvailable,
          }));
        },
        (err) => {
          setUsersSearchState((prevSearchState) => ({
            ...prevSearchState,
            loading: false,
          }));
          toast.error(err.message);
        },
      );
    },
    250,
    [forId, forType, usingRole._id],
  );

  const getAssignedUsers = useCallback(
    (query: string, page: number) => {
      setUsersSearchState((prevSearchState) => ({
        ...prevSearchState,
        loading: true,
        query,
        page,
      }));
      fetchAssignedUsers(query, page);
    },
    [fetchAssignedUsers],
  );

  const fetchPotentialUsers = useThrottle(
    (query: string, page: number) => {
      if (!forId || !forType) return;

      api.roles.getPotentialRoleUsers(
        forType,
        forId,
        usingRole._id,
        { page, ...(query ? { query } : {}) },
        (data) => {
          setPotentialUsersSearchState((prevSearchState) => ({
            ...prevSearchState,
            loading: false,
          }));
          setPotentialUserState({
            users: data.users as PotentialRoleUser[],
            total: data.total,
            nextPageAvailable: data.nextPageAvailable,
            prevPageAvailable: data.prevPageAvailable,
          });
        },
        (err) => {
          setPotentialUsersSearchState((prevSearchState) => ({
            ...prevSearchState,
            loading: false,
          }));
          toast.error(err.message);
        },
      );
    },
    250,
    [forId, forType, usingRole._id],
  );

  const getPotentialUsers = useCallback(
    (query: string, page: number) => {
      setPotentialUsersSearchState((prevSearchState) => ({
        ...prevSearchState,
        loading: true,
        query,
        page,
      }));
      fetchPotentialUsers(query, page);
    },
    [fetchPotentialUsers],
  );

  useEffect(() => {
    getPotentialUsers("", 1);
    getAssignedUsers("", 1);
  }, [getPotentialUsers, getAssignedUsers]);

  const updateUserRoles = useCallback((userId: string, roleId: string) => {
    setUsersState((prevUsersState) => ({
      ...prevUsersState,
      users: prevUsersState.users.map((prevUser) => {
        if (prevUser._id === userId) {
          return {
            ...prevUser,
            roleIds: prevUser.roleIds?.includes(roleId)
              ? prevUser.roleIds.filter((role) => role !== roleId)
              : [...(prevUser.roleIds ?? []), roleId],
          };
        }
        return prevUser;
      }),
    }));
    setPotentialUserState((prevUsersState) => ({
      ...prevUsersState,
      users: prevUsersState.users.map((prevUser) => {
        if (prevUser._id === userId) {
          return {
            ...prevUser,
            roleIds: prevUser.roleIds?.includes(roleId)
              ? prevUser.roleIds.filter((role) => role !== roleId)
              : [...(prevUser.roleIds ?? []), roleId],
          };
        }
        return prevUser;
      }),
    }));
  }, []);

  const assignRoleToUser = useCallback(
    (userId: string) => {
      api.roles.assignRole(
        forType,
        usingForId,
        usingRole._id,
        [userId],
        () => {
          toast.success(`Role ${usingRole.name} assigned to user`);
          updateUserRoles(userId, usingRole._id);
          getAssignedUsers(usersSearchState.query, usersSearchState.page);
          setRoles((prevRoles) =>
            prevRoles.map((role) => {
              if (role._id === usingRole._id) {
                return { ...role, userCount: (role.userCount ?? 0) + 1 };
              }
              return role;
            }),
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [
      forType,
      setRoles,
      updateUserRoles,
      usingForId,
      usingRole._id,
      usingRole.name,
      usersSearchState,
      getAssignedUsers,
    ],
  );

  const unassignRoleFromUser = useCallback(
    (userId: string) => {
      api.roles.unassignRole(
        forType,
        usingForId,
        usingRole._id,
        [userId],
        () => {
          toast.success(`Role ${usingRole?.name} unassigned from user`);
          updateUserRoles(userId, usingRole._id);
          setRoles((prevRoles) =>
            prevRoles.map((role) => {
              if (role._id === usingRole._id) {
                return { ...role, userCount: (role.userCount ?? 0) - 1 };
              }
              return role;
            }),
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [forType, setRoles, updateUserRoles, usingForId, usingRole],
  );

  const onSelectUser = useCallback(
    (selectedUser: OpenAPI.Schemas["users"] | null) => {
      if (!selectedUser) return;
      const userHasRole = selectedUser.roleIds?.includes(usingRole._id);
      if (userHasRole) {
        if (user?._id === selectedUser._id) {
          util
            .confirm(
              "Unassign yourself",
              "Are you sure you want to unassign yourself from this role? This might result in you losing access to this resource.",
            )
            .then(() => {
              unassignRoleFromUser(selectedUser._id);
            })
            .catch(() => {});
        } else {
          unassignRoleFromUser(selectedUser._id);
        }
      } else {
        assignRoleToUser(selectedUser._id);
      }
    },
    [assignRoleToUser, unassignRoleFromUser, usingRole._id, user?._id],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      {usersState?.users?.length > 0 ? (
        <div>
          <h5>Currently assigned users</h5>
          <ConfigurableTable
            tableKey="roleEditorAssignment"
            data={usersState.users}
            keyExtractor={(user) => user._id}
            selectedKeys={assignedUsersWithRole}
            onSelect={onSelectUser}
            preventSelectAll
            columns={[
              {
                key: "image",
                name: "",
                settingName: "Image",
                width: 40,
                center: true,
                render: ({ item }) => (
                  <Link to={`/users/${item._id}`}>
                    <ImageWithFallback
                      style={{ objectFit: "cover" }}
                      avatar
                      src={util.avatarUrl(item)}
                      fallbackSrc={util.avatarUrl()}
                    />
                  </Link>
                ),
              },
              {
                key: "firstName",
                name: "First name",
                render: ({ item }) => (
                  <Link to={`/users/${item._id}`}>
                    {item.profile.firstName}
                  </Link>
                ),
              },
              {
                key: "lastName",
                name: "Last name",
                render: ({ item }) => (
                  <Link to={`/users/${item._id}`}>{item.profile.lastName}</Link>
                ),
              },
              {
                key: "email",
                name: "Email",
                render: ({ item }) => item.emails?.[0]?.address ?? "",
                isVisible: canViewDashboard && canListUsers,
              },
              {
                key: "department",
                name: "Department",
                render: ({ item }) => item.profile.department,
              },
            ]}
          />
        </div>
      ) : null}
      {usersState.prevPageAvailable || usersState.nextPageAvailable ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            activePage={usersSearchState.page}
            totalPages={Math.ceil(usersState.total / 10)}
            onPageChange={(_, { activePage }) => {
              getAssignedUsers(usersSearchState.query, activePage as number);
            }}
            ellipsisItem={null}
          />
        </div>
      ) : null}
      <h5>Assign new users</h5>
      <Input
        icon="search"
        placeholder="Search users..."
        value={potentialUsersSearchState.query}
        fluid
        onChange={(e) => {
          getPotentialUsers(e.target.value, 1);
        }}
        style={{ marginBottom: 10 }}
      />
      <ConfigurableTable
        tableKey="roleEditorAssignment"
        loading={potentialUsersSearchState.loading}
        data={potentialUserState.users as OpenAPI.Schemas["users"][]}
        keyExtractor={(user) => user._id}
        selectedKeys={potentialUsersWithRole}
        onSelect={onSelectUser}
        preventSelectAll
        columns={[
          {
            key: "image",
            name: "",
            settingName: "Image",
            width: 40,
            center: true,
            render: ({ item }) => (
              <Link to={`/users/${item._id}`}>
                <ImageWithFallback
                  style={{ objectFit: "cover" }}
                  avatar
                  src={util.avatarUrl(item)}
                  fallbackSrc={util.avatarUrl()}
                />
              </Link>
            ),
          },
          {
            key: "firstName",
            name: "First name",
            render: ({ item }) => (
              <Link to={`/users/${item._id}`}>{item.profile.firstName}</Link>
            ),
          },
          {
            key: "lastName",
            name: "Last name",
            render: ({ item }) => (
              <Link to={`/users/${item._id}`}>{item.profile.lastName}</Link>
            ),
          },
          {
            key: "email",
            name: "Email",
            render: ({ item }) => item.emails?.[0]?.address ?? "",
            isVisible: canViewDashboard && canListUsers,
          },
          {
            key: "department",
            name: "Department",
            render: ({ item }) => item.profile.department,
          },
        ]}
      />

      {potentialUserState.prevPageAvailable ||
      potentialUserState.nextPageAvailable ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            activePage={potentialUsersSearchState.page}
            totalPages={Math.ceil(potentialUserState.total / 10)}
            onPageChange={(_, { activePage }) => {
              getPotentialUsers(
                potentialUsersSearchState.query,
                activePage as number,
              );
            }}
            ellipsisItem={null}
          />
        </div>
      ) : null}
    </div>
  );
};

export default RoleEditorAssignment;
